import { useLocation, Link } from "react-router-dom";
import {
    LayoutDashboard, Users, FileText, Wallet, Globe, Settings2, FileCode, InfoIcon
} from "lucide-react";
import { FiHome } from "react-icons/fi";
import { RiAdvertisementLine } from "react-icons/ri";
import { IoAnalyticsOutline } from "react-icons/io5";
import { HiOutlineColorSwatch } from "react-icons/hi";
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Currency } from "lucide-react";
import SidebarLogo from "../Common/SidebarLogo";

const AdminSidebar = () => {
    const [isOpen, setIsOpen] = useState(true);
    const [hoveredItem, setHoveredItem] = useState(null);
    const location = useLocation();

    const isActive = (path) => location.pathname === path;

    const menuItems = [
        { path: "/home", label: "Home", icon: <FiHome size={18} /> },
        { path: "/overview", label: "Overview", icon: <LayoutDashboard size={18} /> },
        { path: "/publishers", label: "Publishers", icon: <Users size={18} /> },
        { path: "/analytics", label: "Analytics", icon: <IoAnalyticsOutline size={18} /> },
        { path: "/adunits", label: "Adunits", icon: <RiAdvertisementLine size={18} /> },
        { path: "/transactions", label: "Transactions", icon: <Wallet size={18} /> },
        { path: "/qa-tickets", label: "QA Tickets", icon: <HiOutlineColorSwatch size={18} /> },
        { path: "/invoice", label: "Invoice", icon: <FileText size={18} /> },
        { path: "/payment-info", label: "Payment Info", icon: <Currency size={18} /> },
        { path: "/website", label: "Website/Apps", icon: <Globe size={18} /> },
        { path: "/AdsTxtConfiguration", label: "AdsTxt Config", icon: <Settings2 size={18} /> },
        { path: "/AdsCodeConfiguration", label: "AdsCode Config", icon: <FileCode size={18} /> },
        { path: "/notes", label: "Notes", icon: <InfoIcon size={18} /> },
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (window.innerWidth < 768 && !event.target.closest('.sidebar-container')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsOpen(true);
            } else {
                setIsOpen(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const sidebarWidth = isOpen ? 220 : 72;
        window.dispatchEvent(new CustomEvent("sidebar-resize", { detail: sidebarWidth }));
    }, [isOpen]);

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gradient-to-br from-gray-900 to-indigo-900 z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="relative flex">
        <motion.div
          className={`sidebar-container fixed top-0 left-0 h-full bg-gradient-to-br from-[#0f172a]/90 via-[#1e293b]/90 to-[#0f172a]/90 backdrop-blur-lg border-r border-gray-700/40 shadow-[0_0_15px_rgba(99,102,241,0.4)] z-50 md:relative transition-all duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] ${
            isOpen ? "w-[240px]" : "w-[80px]"
          }`}
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Logo */}
          <motion.div className="flex justify-center items-center p-5 border-b border-gray-700/50" whileHover={{ scale: 1.02 }}>
            <AnimatePresence mode="wait">
              {isOpen ? (
                <SidebarLogo />
              ) : (
                <motion.div
                  key="logo-icon"
                  className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </AnimatePresence>
          </motion.div>

          {/* Menu Items */}
          <ul className="space-y-2 pt-6 px-3">
            {menuItems.map(({ path, label, icon }) => (
              <motion.li
                key={path}
                onHoverStart={() => setHoveredItem(path)}
                onHoverEnd={() => setHoveredItem(null)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to={path} className="block">
                  <motion.div
                    className={`flex items-center gap-3 p-3 mx-1 rounded-lg relative transition-all duration-300 group ${
                      isActive(path)
                        ? "bg-gradient-to-r from-indigo-500/30 to-purple-600/30 shadow-md"
                        : "hover:bg-indigo-500/10 hover:shadow-[0_0_8px_rgba(99,102,241,0.2)]"
                    }`}
                  >
                    {isActive(path) && (
                      <motion.div
                        className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-purple-400 to-indigo-400 rounded-r animate-pulse"
                        layoutId="activeIndicator"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}

                    <motion.div
                      className="flex-shrink-0"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                        color: isActive(path)
                          ? "#a78bfa"
                          : hoveredItem === path
                          ? "#c4b5fd"
                          : "#9ca3af",
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {React.cloneElement(icon, { size: 18 })}
                    </motion.div>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.span
                          className="text-sm font-medium"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{
                            opacity: 1,
                            x: 0,
                            color: isActive(path)
                              ? "#f3e8ff"
                              : hoveredItem === path
                              ? "#d8b4fe"
                              : "#d1d5db",
                          }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {label}
                        </motion.span>
                      )}
                    </AnimatePresence>

                    {!isOpen && hoveredItem === path && (
                      <motion.div
                        className="absolute left-full ml-3 px-3 py-2 bg-gray-800 text-white text-xs font-medium rounded-lg shadow-xl whitespace-nowrap z-50 border border-gray-700"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                      >
                        {label}
                        <div className="absolute w-2 h-2 bg-gray-800 rotate-45 -left-1 top-1/2 -translate-y-1/2 border-l border-t border-gray-700" />
                      </motion.div>
                    )}
                  </motion.div>
                </Link>
              </motion.li>
            ))}
          </ul>


          {/* Toggle Button */}
          <motion.button
            className="absolute -right-3.5 top-6 bg-gradient-to-br from-gray-800 to-gray-900 p-1.5 rounded-full shadow-xl z-10 border border-gray-700 hidden md:flex items-center justify-center"
            onClick={() => setIsOpen(!isOpen)}
            whileHover={{ scale: 1.1, backgroundColor: "rgba(55, 65, 81, 0.8)" }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="text-gray-300"
              initial={{ rotate: isOpen ? 0 : 180 }}
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={isOpen ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} />
              </svg>
            </motion.div>
          </motion.button>
        </motion.div>

        {/* Mobile Toggle Button */}
        <motion.button
          className="md:hidden p-2.5 text-white fixed top-4 left-4 z-50 bg-gradient-to-br from-gray-800 to-gray-900 backdrop-blur-sm rounded-xl shadow-lg border border-gray-700"
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: 90 }}
                animate={{ rotate: 0 }}
                exit={{ rotate: 90 }}
                className="text-gray-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: -90 }}
                animate={{ rotate: 0 }}
                exit={{ rotate: -90 }}
                className="text-gray-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </>
  );
};

export default AdminSidebar;